import React, { useEffect, useState, ReactNode } from "react";
import { usePost } from "../API";
import { Card, CardBody, Heading, Paragraph, Spinner, Badge, Alert } from "@aidkitorg/component-library";

export interface WorkflowReviewProps {
  distroCommitId: string;
  workflowName: string;
  title: string;
  renderSummary: (workflow: any) => ReactNode;
  pollingInterval?: number;
}

export default function WorkflowReview({ 
  distroCommitId, 
  workflowName, 
  title, 
  renderSummary, 
  pollingInterval = 5000 
}: WorkflowReviewProps) {
  const getWorkflow = usePost('/distro-review/workflow');
  const [workflow, setWorkflow] = useState<any>(undefined);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  // Load workflow on mount
  useEffect(() => {
    const loadWorkflow = async () => {
      setIsLoadingWorkflow(true);
      setWorkflowError(null);
      try {
        const result = await getWorkflow({ 
          workflowName,
          distroCommitId
        });
        if (result.error) {
          setWorkflowError(result.error);
        } else if (result.message) {
          setWorkflowError(result.message);
        } else {
          setWorkflow(result.workflow);
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        setWorkflowError('Failed to load workflow information');
      } finally {
        setIsLoadingWorkflow(false);
      }
    };

    loadWorkflow();
  }, [getWorkflow, distroCommitId, workflowName]);

  // Poll for updates while workflow is in progress
  useEffect(() => {
    if (!distroCommitId || !workflow || !['in_progress', 'pending', 'scheduled'].includes(workflow.status)) {
      return;
    }
    
    const pollingIntervalId = setInterval(async () => {
      try {
        const result = await getWorkflow({ 
          workflowName,
          distroCommitId
        });
        if (!result.error && result.workflow) {
          setWorkflow(result.workflow);
        }
      } catch (error) {
        console.error('Error polling workflow status:', error);
      }
    }, pollingInterval);
    
    return () => clearInterval(pollingIntervalId);
  }, [workflow, getWorkflow, distroCommitId, workflowName, pollingInterval]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'scheduled': return 'info';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'approved': return 'success';
      default: return 'neutral';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <Heading level="h2" className="text-lg font-semibold mb-4">{title}</Heading>
      {isLoadingWorkflow ? (
        <Spinner className="p-8" />
      ) : workflowError ? (
        <Alert
          title="Error"
          variant="danger"
          className="mb-6"
        >
          {workflowError}
        </Alert>
      ) : workflow ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <Badge variant={getStatusBadgeVariant(workflow.status)}>{workflow.status}</Badge>
            <span className="text-sm text-gray-500">
              {workflow.timeline.completedAt ? 
                `Completed ${formatDate(workflow.timeline.completedAt)}` :
                `Started ${formatDate(workflow.timeline.startedAt)}`
              }
            </span>
          </div>
          
          {workflow.status === 'completed' && renderSummary(workflow)}
          
          {workflow.status === 'in_progress' && (
            <Alert
              title="Processing"
              variant="primary"
            >
              Workflow is in progress. Results will appear here when complete.
            </Alert>
          )}
          
          {workflow.status === 'failed' && (
            <Alert
              title="Workflow Failed"
              variant="danger"
            >
              The workflow failed to complete. 
              {workflow.result?.error && `: ${workflow.result.error}`}
            </Alert>
          )}

          {workflow.status === 'pending' && (
            <Alert
              title="Pending"
              variant="warning"
            >
              Workflow is queued. It may be waiting for other workflows to complete before it can start.
            </Alert>
          )}

          {workflow.status === 'scheduled' && (
            <Alert
              title="Scheduled"
              variant="primary"
            >
              Workflow has been scheduled and will begin processing shortly.
            </Alert>
          )}

          {workflow.status === 'cancelled' && (
            <Alert
              title="Cancelled"
              variant="danger"
            >
              Workflow has been cancelled due to a dependency workflow failing.
            </Alert>
          )}
        </div>
      ) : (
        <Paragraph>No workflow information available.</Paragraph>
      )}
    </>
  );
} 
